import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Image as ImageIcon, RefreshCw } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import Button from './ui/Button';
import { Select } from './ui/Select';
import Badge from './ui/Badge';

/**
 * T2.8 (tor 2, 18.10.2026): A+ jako płatny dodatek w Gotowości.
 * 3 grafiki na produkt (hero · w użyciu · cechy) ze zdjęcia z pliku klienta. Cena: RPC aplus_price() kredytów / produkt,
 * maks. aplus_max_products() produktów na zlecenie. Opłata raz na zlecenie (edge generate-aplus), zwrot, gdy silnik nie przyjmie.
 * Wynik: adresy grafik w pliku z Historii (kolumny A+) i ZIP w Historii.
 */
const APLUS_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-aplus`;
const IMG_HINT = /(zdj|foto|photo|image|img|obraz|picture|url)/i;

const STATUS = {
  idle:       { label: 'nie zamówione', tone: 'neutral' },
  processing: { label: 'w toku',        tone: 'brand' },
  done:       { label: 'gotowe',        tone: 'success' },
  error:      { label: 'błąd',          tone: 'danger' },
  partial:    { label: 'częściowo',     tone: 'warning' },
};

export default function AplusPanel({ orderId, onCreditsChange = null }) {
  const [order, setOrder] = useState(null);
  const [cols, setCols] = useState([]);
  const [col, setCol] = useState('');
  const [price, setPrice] = useState(5);
  const [max, setMax] = useState(5);
  const [images, setImages] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: o }, { data: first }, { count }] = await Promise.all([
      supabase.from('orders').select('id, product_count, language, aplus_status, aplus_image_column, aplus_expected').eq('id', orderId).maybeSingle(),
      supabase.from('batch_results').select('original_data').eq('order_id', orderId).order('row_index').range(0, 0),
      supabase.from('aplus_results').select('id', { count: 'exact', head: true }).eq('order_id', orderId),
    ]);
    setOrder(o || null);
    setImages(count || 0);
    const keys = Object.keys(first?.[0]?.original_data || {}).filter(k => k && k !== 'null');
    setCols(keys);
    setCol(c => c || o?.aplus_image_column || keys.find(k => IMG_HINT.test(k)) || '');
  }, [orderId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.rpc('aplus_price').then(({ data }) => { const n = Number(data); if (n > 0) setPrice(n); }).catch(() => {});
    supabase.rpc('aplus_max_products').then(({ data }) => { const n = Number(data); if (n > 0) setMax(n); }).catch(() => {});
  }, []);
  // W toku → odświeżaj co 15 s.
  useEffect(() => {
    if (order?.aplus_status !== 'processing') return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [order?.aplus_status, load]);

  if (!order) return null;
  const products = Math.max(1, Math.min(order.product_count || 1, max));
  const cost = price * products;
  const st = STATUS[order.aplus_status] || STATUS.idle;

  const run = async () => {
    if (!col) return toast.error('Wskaż kolumnę ze zdjęciem produktu.');
    const retry = order.aplus_status === 'error' || order.aplus_status === 'partial';
    if (!window.confirm(retry
      ? 'Ponowić A+? Gdy A+ dla tego zlecenia było już opłacone, ponowienie jest bez dopłaty.'
      : `Zamówić A+ dla ${products} ${products === 1 ? 'produktu' : 'produktów'}? Koszt: ${cost} kredytów (${price} za produkt, 3 grafiki).`)) return;
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(APLUS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: process.env.REACT_APP_SUPABASE_ANON_KEY, Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ order_id: orderId, image_column: col, language: order.language }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.status === 402) throw new Error(`Za mało kredytów — potrzeba ${d.need ?? cost}.`);
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      toast.success('A+ zamówione — grafiki powstają w tle (kilka minut).');
      onCreditsChange?.();
      load();
    } catch (e) { toast.error(`A+: ${e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mb-5">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> A+ — grafiki produktu <Badge variant={st.tone} size="sm">{st.label}</Badge></CardTitle>
            <CardDescription>3 grafiki na produkt (hero · w użyciu · cechy) ze zdjęcia z pliku. {price} kredytów za produkt, maks. {max} produktów na zlecenie. Pliki pobierzesz w Historii (ZIP i adresy w pliku wyników).</CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={load} leftIcon={<RefreshCw className="w-4 h-4" />}>Odśwież</Button>
        </div>
      </CardHeader>
      <CardBody className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
        {order.aplus_status === 'done' || order.aplus_status === 'processing' ? (
          <p className="text-sm text-ink-600 dark:text-ink-300">
            {order.aplus_status === 'done' ? `Gotowe: ${images} grafik.` : `W toku: ${images}${order.aplus_expected ? ` z ${order.aplus_expected}` : ''} grafik.`} Pobierz w Historii → „A+ ZIP”.
          </p>
        ) : (
          <>
            <div>
              <div className="text-sm font-medium text-ink-700 dark:text-ink-300 mb-1.5">Kolumna ze zdjęciem produktu</div>
              <Select value={col} onChange={setCol} options={cols} placeholder="Wybierz kolumnę…" />
            </div>
            <Button onClick={run} loading={busy} disabled={!col}>
              {order.aplus_status === 'error' || order.aplus_status === 'partial' ? 'Ponów A+' : `Zamów A+ · ${cost} kredytów`}
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}
